import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, ShieldCheck, ShieldAlert, Send, PlayCircle, RefreshCw, Lock, ExternalLink } from "lucide-react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * ResidualPayoutSection — HQ admin section for the owner-only Residual Payout
 * (real money via Stripe Connect Transfers). Separate universe from MPire Buck$.
 *
 * Contents:
 *   1. Stripe Connect status card + onboarding link
 *   2. Settings card (percent, day, time, active) — OTP-gated
 *   3. Run-now button (OTP-gated, for admin testing)
 *   4. Payout history table (date, amount, recipient, transfer id, status)
 */

const DAYS = [
  { v: "mon", l: "Monday" },
  { v: "tue", l: "Tuesday" },
  { v: "wed", l: "Wednesday" },
  { v: "thu", l: "Thursday" },
  { v: "fri", l: "Friday" },
  { v: "sat", l: "Saturday" },
  { v: "sun", l: "Sunday" },
];

function StatusChip({ ok, label, warn }) {
  const cls = ok
    ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
    : warn
      ? "bg-amber-100 text-amber-800 border border-amber-200"
      : "bg-red-100 text-red-800 border border-red-200";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${cls}`}>
      {ok ? <ShieldCheck size={10} /> : <ShieldAlert size={10} />}
      {label}
    </span>
  );
}

export default function ResidualPayoutSection() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // OTP state
  const [otpTarget, setOtpTarget] = useState("primary");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [session, setSession] = useState(null); // {token, expires_at}

  // Draft settings the user is editing
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [onboardingBusy, setOnboardingBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get("/residual-payout/settings").then((r) => r.data),
        api.get("/residual-payout/history").then((r) => r.data).catch(() => []),
      ]);
      setSettings(s);
      setHistory(h);
      setDraft({
        percent: s.percent,
        schedule_day: s.schedule_day,
        schedule_hour: s.schedule_hour,
        schedule_minute: s.schedule_minute,
        active: s.active,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load Residual Payout settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sendOtp = async () => {
    try {
      await api.post("/residual-payout/otp/send", { target: otpTarget });
      setOtpSent(true);
      toast.success(`Verification code sent to ${otpTarget === "primary" ? settings.owner_email_primary : settings.owner_email_backup}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send code");
    }
  };

  const verifyOtp = async () => {
    try {
      const { data } = await api.post("/residual-payout/otp/verify", { code: otpCode.trim() });
      setSession({ token: data.session_token, expires_at: Date.now() + data.expires_in_sec * 1000 });
      setOtpSent(false);
      setOtpCode("");
      toast.success("Unlocked — you can now edit settings.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    }
  };

  const unlocked = !!session && Date.now() < session.expires_at;

  const saveSettings = async () => {
    if (!unlocked) { toast.error("Unlock with OTP first"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/residual-payout/settings", {
        session_token: session.token,
        ...draft,
      });
      setSettings((prev) => ({ ...(prev || {}), ...data }));
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startOnboarding = async () => {
    if (!unlocked) { toast.error("Unlock with OTP first"); return; }
    setOnboardingBusy(true);
    try {
      const { data } = await api.post("/residual-payout/onboarding/start", {
        session_token: session.token,
        return_url: `${window.location.origin}/?residual=return`,
        refresh_url: `${window.location.origin}/?residual=refresh`,
      });
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.success("Opening Stripe onboarding in a new tab…");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Onboarding failed");
    } finally {
      setOnboardingBusy(false);
    }
  };

  const refreshOnboarding = async () => {
    try {
      const { data } = await api.post("/residual-payout/onboarding/refresh-status");
      // Merge so a partial payload can't clobber schedule/active fields.
      setSettings((prev) => ({ ...(prev || {}), ...data }));
      toast.success(data.stripe_payouts_enabled ? "Payouts enabled ✓" : "Status refreshed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Refresh failed");
    }
  };

  const runNow = async () => {
    if (!unlocked) { toast.error("Unlock with OTP first"); return; }
    if (!window.confirm("Trigger a Residual Payout right now? This will move real money if onboarding is complete.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/residual-payout/run-now", { session_token: session.token });
      toast.success(`Payout ${data.status} — $${Number(data.amount || 0).toFixed(2)}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !settings || !draft) {
    return (
      <Card className="p-5 mt-4">
        <p className="text-sm text-gray-500">Loading Residual Payout…</p>
      </Card>
    );
  }

  return (
    <Card className="p-5 mt-4" data-testid="residual-payout-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <Banknote size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow">Residual Payout · Owner only</p>
          <p className="text-xs text-gray-500">
            Weekly Stripe Connect transfer of {settings.percent}% of trailing revenue.{" "}
            <strong>Not related to MPire Buck$.</strong> This moves real money.
          </p>
        </div>
        {unlocked ? (
          <StatusChip ok label="Unlocked" />
        ) : (
          <StatusChip ok={false} warn label="Locked" />
        )}
      </div>

      {/* Onboarding + status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg border border-[var(--border)] p-3 bg-white">
          <p className="label-eyebrow mb-1">Owner Stripe Connect</p>
          <p className="font-mono text-xs text-neutral-700 break-all mb-2">
            {settings.stripe_account_id || "— not connected —"}
          </p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            <StatusChip ok={settings.stripe_details_submitted} label={settings.stripe_details_submitted ? "Onboarded" : "Not onboarded"} />
            <StatusChip ok={settings.stripe_payouts_enabled} label={settings.stripe_payouts_enabled ? "Payouts on" : "Payouts off"} />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={startOnboarding}
              disabled={onboardingBusy || !unlocked}
              data-testid="residual-onboarding-start"
              title={unlocked ? "" : "Unlock with OTP first"}
            >
              <ExternalLink size={12} className="mr-1" /> {settings.stripe_account_id ? "Continue" : "Start"} onboarding
            </Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={refreshOnboarding} data-testid="residual-refresh-status">
              <RefreshCw size={12} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-[var(--border)] p-3 bg-white">
          <p className="label-eyebrow mb-1">Recipient</p>
          <p className="text-xs text-neutral-500">Primary</p>
          <p className="text-sm font-medium truncate">{settings.owner_email_primary}</p>
          <p className="text-xs text-neutral-500 mt-1.5">Backup</p>
          <p className="text-sm text-neutral-700 truncate">{settings.owner_email_backup}</p>
        </div>

        <div className="rounded-lg border border-[var(--border)] p-3 bg-white">
          <p className="label-eyebrow mb-1">Next scheduled run</p>
          <p className="text-sm font-semibold">
            {settings.schedule_day?.toUpperCase()} @ {String(settings.schedule_hour).padStart(2, "0")}:{String(settings.schedule_minute).padStart(2, "0")} {settings.tz}
          </p>
          <p className="text-xs text-neutral-500 mt-1">Automation is {settings.active ? "ACTIVE" : "PAUSED"}</p>
        </div>
      </div>

      {/* OTP unlock */}
      {!unlocked && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={14} className="text-amber-800" />
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">
              OTP challenge — settings, onboarding, and run-now are locked
            </p>
          </div>
          {!otpSent ? (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Send code to</Label>
                <Select value={otpTarget} onValueChange={setOtpTarget}>
                  <SelectTrigger className="bg-white" data-testid="residual-otp-target"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary · {settings.owner_email_primary}</SelectItem>
                    <SelectItem value="backup">Backup · {settings.owner_email_backup}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="btn-brand" onClick={sendOtp} data-testid="residual-otp-send">
                <Send size={14} className="mr-1" /> Send code
              </Button>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">6-digit code</Label>
                <Input
                  className="bg-white font-mono tracking-widest"
                  maxLength={8}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  data-testid="residual-otp-code"
                  autoFocus
                />
              </div>
              <Button className="btn-brand" onClick={verifyOtp} disabled={otpCode.trim().length < 4} data-testid="residual-otp-verify">
                Unlock
              </Button>
              <Button variant="ghost" onClick={() => setOtpSent(false)}>Cancel</Button>
            </div>
          )}
        </div>
      )}

      {/* Settings */}
      <div className={`grid grid-cols-1 sm:grid-cols-4 gap-3 items-end mb-3 ${unlocked ? "" : "opacity-60 pointer-events-none"}`}>
        <div>
          <Label className="text-xs">Payout percent (%)</Label>
          <Input
            type="number"
            step="0.01"
            value={draft.percent}
            onChange={(e) => setDraft({ ...draft, percent: Number(e.target.value) })}
            data-testid="residual-percent"
          />
        </div>
        <div>
          <Label className="text-xs">Day of week</Label>
          <Select value={draft.schedule_day} onValueChange={(v) => setDraft({ ...draft, schedule_day: v })}>
            <SelectTrigger data-testid="residual-day"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Hour (0–23)</Label>
          <Input
            type="number"
            min={0}
            max={23}
            value={draft.schedule_hour}
            onChange={(e) => setDraft({ ...draft, schedule_hour: Number(e.target.value) })}
            data-testid="residual-hour"
          />
        </div>
        <div>
          <Label className="text-xs">Minute (0–59)</Label>
          <Input
            type="number"
            min={0}
            max={59}
            value={draft.schedule_minute}
            onChange={(e) => setDraft({ ...draft, schedule_minute: Number(e.target.value) })}
            data-testid="residual-minute"
          />
        </div>
      </div>

      <div className={`flex flex-wrap items-center gap-2 mb-5 ${unlocked ? "" : "opacity-60 pointer-events-none"}`}>
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            data-testid="residual-active"
          />
          <span>Automation active</span>
        </label>
        <div className="flex-1" />
        <Button variant="outline" onClick={runNow} disabled={running || !unlocked} data-testid="residual-run-now">
          <PlayCircle size={14} className="mr-1" /> {running ? "Running…" : "Run now"}
        </Button>
        <Button className="btn-brand" onClick={saveSettings} disabled={saving || !unlocked} data-testid="residual-save">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>

      {/* History */}
      <div>
        <p className="label-eyebrow mb-2">Residual Payout history</p>
        <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-right p-2">Period revenue</th>
                <th className="text-right p-2">Amount</th>
                <th className="text-left p-2">Recipient</th>
                <th className="text-left p-2">Stripe transfer id</th>
                <th className="text-left p-2">Status</th>
              </tr>
            </thead>
            <tbody data-testid="residual-history-body">
              {history.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-400 text-xs">No payouts yet.</td></tr>
              )}
              {history.map((e, i) => {
                const statusCls = e.status === "successful" ? "text-emerald-700 bg-emerald-100"
                  : e.status === "failed"                    ? "text-red-700 bg-red-100"
                  : e.status === "skipped"                   ? "text-neutral-600 bg-neutral-100"
                  :                                            "text-amber-700 bg-amber-100";
                return (
                  <tr key={i} className="border-t border-[var(--border)]">
                    <td className="p-2 font-mono text-[11px] whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="p-2 text-right tabular-nums">${Number(e.period_revenue || 0).toFixed(2)}</td>
                    <td className="p-2 text-right font-semibold tabular-nums">${Number(e.amount || 0).toFixed(2)}</td>
                    <td className="p-2 truncate max-w-[220px]">{e.recipient_email || "—"}</td>
                    <td className="p-2 font-mono text-[11px] truncate max-w-[220px]">{e.stripe_transfer_id || "—"}</td>
                    <td className="p-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${statusCls}`}>
                        {e.status || "—"}
                      </span>
                      {e.error && <div className="text-[10px] text-red-600 italic mt-0.5 truncate max-w-[220px]" title={e.error}>{e.error}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
